import { ReactNode, Suspense } from "react"
import { Body, LoadingBody } from "@/components/wysiwyg/Body"
import { DrupalNodeArticle, DrupalNodeBasicPage, DrupalNodeRelease } from "@/types"
import { VersionStatusMessage } from "./VersionStatusMessage.client"
import { PageInterface } from "@/components/page/PageInterface"
import { PreviewTag } from "./PreviewBar.client"
import { PreviewBarContextProps } from "@/contexts/PreviewBarProvider"
import { searchParamsCache } from "@/lib/searchParams"
import HtmlDiff from 'htmldiff-js';
import { SkipToContentMarker } from "@/contexts/SkipToContentProvider"
import { TocMarker } from "@/contexts/TocProvider"
import { getTitle } from "@/components/app/drupal/header"
import parse from "html-react-parser"

export const PageContent = async ({resource, revision, children, footer, version}: PageInterface<DrupalNodeBasicPage | DrupalNodeArticle | DrupalNodeRelease> & {
  children?: ReactNode
  footer?: ReactNode
}) => {
  const mode = searchParamsCache.get('previewMode')


  const node = revision ?? resource
  const previewResource = mode == 'revision' ? node : resource

  let bodyContent;

  switch (mode) {
    case 'diff':
      bodyContent = diff(resource.body?.processed, revision?.body?.processed ?? '');
      break;
    case 'revision':
      bodyContent = revision?.body?.processed ?? resource.body?.processed
      break;
    default:
    case 'current':
      bodyContent = resource.body?.processed ?? ''
      break;
  }

  const previewModes:PreviewBarContextProps['preview']['modes'] = ['current']

  if (revision && revision.drupal_internal__vid != resource.drupal_internal__vid) {
    previewModes.push('revision', 'diff')
  }

  const body = bodyContent?.match('<body>') !== null ? bodyContent?.replaceAll('<body>','').replaceAll('</body>','') : bodyContent;
  
  const titleContent = await getTitle(resource, revision ?? undefined, false)

  return (
    <>
      <TocMarker enabled={bodyContent?.match('id="contents"') !== null} />
      <PreviewTag mode={mode ?? previewModes.includes('revision' ) ? 'revision' : 'current'} modes={previewModes} resource={previewResource} state={previewResource.moderation_state as 'draft' | 'published'}>
          {children}
          <VersionStatusMessage />
          <Suspense fallback={<LoadingBody />}>
            <SkipToContentMarker id='toContent' />
            <h1 className="sr-only">{parse(titleContent)}</h1>
            <Body value={body || ''} sectionScroll={true} className="max-w-6xl meta-content mx-3" context={{
              resource,
              version
            }} />
          </Suspense>
          {footer}
      </PreviewTag>
    </>
  )
}

function diff(orig: string, incoming: string) {
  return HtmlDiff.execute(
    orig.replaceAll(/>[\s\n\t]+</g, '><'), 
    incoming.replaceAll(/>[\s\n\t]+</g, '><')
  )
}